import { mdiAccountMultiple } from '@mdi/js';
import { Currency } from 'common/classes/currency';
import { BaseTable, ColumnProp } from 'common/components/table/base';
import { TableTitle } from 'common/components/tables';
import { AccountWithBalance } from 'common/state/accounts/actions';
import React, { useMemo } from 'react';
import { AccountBetResponseMap } from '../customer';

export interface AccountsTableProps {
  accounts?: AccountWithBalance[];
  bets?: AccountBetResponseMap;
  isLoading?: boolean;
  error?: React.ReactNode;
}

export interface AccountTableRow extends AccountWithBalance {
  bets: AccountBetResponseMap[string];
}

export const AccountsTable = ({
  accounts,
  bets,
  isLoading,
  error,
}: AccountsTableProps) => {
  const columns = useMemo(
    (): ColumnProp<AccountTableRow>[] => [
      { key: 'username', title: 'Username' },
      { key: 'system_name', title: 'System' },
      { key: 'currency', title: 'Currency' },
      {
        key: 'pl',
        title: 'P/L',
        valueFormatter: (row) => new Currency(row.pl, row.currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.pl, row.currency).toString({ symbol: false }),
      },
      {
        key: 'balance',
        title: 'Balance',
        valueFormatter: (row) =>
          new Currency(row.balance, row.currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balance, row.currency).toString({ symbol: false }),
      },
      {
        key: 'credit',
        title: 'Credit',
        valueFormatter: (row) =>
          new Currency(row.credit, row.currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.credit, row.currency).toString({ symbol: false }),
      },
      {
        key: 'last_bet',
        title: 'Last Bet',
        valueFormatter: (row) =>
          !bets
            ? 'Loading...'
            : row.bets.results[0]?.placed_at
            ? new Date(row.bets.results[0].placed_at).toLocaleString()
            : '-',
        sortValueGenerator: (row) => row.bets?.results?.[0]?.placed_at,
      },
      {
        key: 'total_bets',
        title: 'Total Bets',
        valueFormatter: (row) =>
          bets ? row.bets.count.toLocaleString() : 'Loading...',
        sortValueGenerator: (row) => row.bets?.count,
      },
    ],
    [bets]
  );

  const rows = useMemo(
    (): AccountTableRow[] =>
      accounts?.map((account) => ({
        ...account,
        bets: bets?.[account.username],
      })),
    [accounts, bets]
  );

  return (
    <>
      <TableTitle icon={mdiAccountMultiple} title="Accounts" />
      <BaseTable
        columns={columns}
        rows={rows || []}
        csvName="Accounts"
        isLoading={isLoading}
        error={error}
        paginate={false}
      />
    </>
  );
};
